import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { AVAILABLE_UNIT_ID, findUnit, unitCaption } from './data/fleet';

// ─────────────────────────────────────────────────────────────────────────────
// Which fleet unit the prototypes are showing, plus the fleet menu's
// open/closed state.
//
// Both live above the router so they survive moving between prototype tabs.
// The menu collapses so a tablet (iPad demo) can give the charts full width.
// ─────────────────────────────────────────────────────────────────────────────

const SIDEBAR_KEY = 'oclab.sidebar';

type FleetContextType = {
  unitId: string;
  setUnitId: (id: string) => void;
  /** e.g. '4-car EMU · DX-04' */
  caption: string;
  /** False when the selected unit has no demo data. */
  hasData: boolean;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
};

const FleetContext = createContext<FleetContextType | null>(null);

function readSidebar(): boolean {
  try {
    const v = window.localStorage.getItem(SIDEBAR_KEY);
    if (v === 'closed') return false;
    if (v === 'open') return true;
  } catch {
    // Storage unavailable — fall through to the default.
  }
  // Default closed on narrow screens so a tablet starts with full-width charts.
  return typeof window !== 'undefined' ? window.innerWidth >= 1200 : true;
}

export const FleetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unitId, setUnitId] = useState(AVAILABLE_UNIT_ID);
  const [sidebarOpen, setSidebarOpen] = useState(readSidebar);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => {
      const next = !prev;
      try { window.localStorage.setItem(SIDEBAR_KEY, next ? 'open' : 'closed'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    unitId,
    setUnitId,
    caption: unitCaption(unitId),
    hasData: findUnit(unitId)?.status === 'ready',
    sidebarOpen,
    toggleSidebar,
  }), [unitId, sidebarOpen, toggleSidebar]);

  return <FleetContext.Provider value={value}>{children}</FleetContext.Provider>;
};

export const useFleet = (): FleetContextType => {
  const ctx = useContext(FleetContext);
  if (!ctx) throw new Error('useFleet must be used inside a FleetProvider');
  return ctx;
};
